import { LegacyApiClient, TOP_UP_API_BASE_URL } from '@mypoz/shared';

import { endSession, sessionToken } from '@/lib/session.js';

/**
 * Baked in at build time, like `apiBaseUrl` next door, and pointed at the vendor's own host by
 * default because that is where this API lives — there is no MyPoz-owned deployment of it.
 *
 * Overridable so a local proxy can be put in front of it if the vendor ever withdraws the CORS
 * headers it sends today. Declared in `globalEnv` in turbo.json for the same reason VITE_API_URL
 * is: change the URL without it and the build replays a cached `dist` aimed at the old one.
 */
export const legacyApiBaseUrl =
  import.meta.env.VITE_LEGACY_API_URL || 'https://www.mypoz.com/OutterApi/Api';

/**
 * The host the API's stored files live on — `uploadImg` answers with site-relative paths like
 * `/Public/Uploads/…` on the same host as the API — so receipt links follow `VITE_LEGACY_API_URL`
 * instead of assuming production (MYP-96).
 */
export const legacyAssetOrigin = new URL(legacyApiBaseUrl, globalThis.location?.href).origin;

/**
 * The customer-facing MyPoz website, for links that send the customer there (the Wallet's "Go to
 * mypoz.com" while top-up is switched off, MYP-106). Deliberately not `legacyAssetOrigin`: that
 * follows the API's host, which may move (a proxy, an `api.` subdomain) while the site stays put.
 */
export const MYPOZ_SITE_URL = 'https://www.mypoz.com';

/**
 * MyPoz's own article on telling general goods from sensitive ones (什么是普货/敏感货？如何分辨？),
 * linked from the goods-type choice on Add/Edit package (MYP-145). Chinese only today, so every
 * language opens the same page.
 */
export const GOODS_TYPE_HELP_URL = `${MYPOZ_SITE_URL}/newwpmypoz/case-studies/%e4%bb%80%e4%b9%88%e6%98%af%e6%99%ae%e8%b4%a7-%e6%95%8f%e6%84%9f%e8%b4%a7%ef%bc%9f%e5%a6%82%e4%bd%95%e5%88%86%e8%be%a8%ef%bc%9f/`;

/**
 * The old website's Security Center (安全中心), where a customer uploads their IC or passport and a
 * photo of their face for MyPoz staff to verify (MYP-142). The new app never handles those images.
 */
export const SECURITY_CENTER_URL = `${MYPOZ_SITE_URL}/index.php/Mypost/Myinfo/security_center.html`;

/**
 * The vendor's legacy OutterApi.
 *
 * `getToken` is the customer's session token (MYP-125: from the old website's `?token=`, or the
 * development token), read fresh for every call; `LegacyApiClient` puts it in the body. A reply
 * that says the token is invalid ends the session (`endSession`), and the app shows its
 * signed-out screen.
 *
 * The timeout is what turns a hung vendor request into an error state a customer can retry from,
 * rather than a spinner that never resolves. Fifteen seconds because this API is not fast.
 */
export const legacyApi = new LegacyApiClient({
  baseUrl: legacyApiBaseUrl,
  getToken: sessionToken,
  onInvalidToken: endSession,
  timeoutMs: 15_000,
});

/**
 * The same API, for uploads (`uploadImg`, MYP-96). Identical except for the timeout: receipts are
 * shrunk before upload, usually well under 1 MB, but a phone on a slow connection can still take
 * longer than the ordinary fifteen seconds. A minute, so a slow upload fails with a message rather
 * than hangs.
 */
export const legacyUploadApi = new LegacyApiClient({
  baseUrl: legacyApiBaseUrl,
  getToken: sessionToken,
  onInvalidToken: endSession,
  timeoutMs: 60_000,
});

/**
 * The same API on the host the vendor's top-up guide gives (MYP-134), for `molpay_pay` only. The
 * address is `TOP_UP_API_BASE_URL` from `@mypoz/shared`, written once, next to the payment-link
 * check that uses the same host. The vendor says mypostonline.com.my is the same server as
 * www.mypoz.com, so the customer's token (sent in the body, as for every call) works the same.
 *
 * Two things this means:
 * - `VITE_LEGACY_API_URL` does **not** move this call. A local proxy put in front of the main API
 *   does not cover `molpay_pay`; it still goes to the vendor's host directly.
 * - The browser calls a second origin. If it is refused there (no CORS headers for
 *   web.mypoz.com), starting a top-up fails with "couldn't reach the server" and nothing is
 *   recorded. Check this before `FIUU_TOPUP_ENABLED` is turned on.
 */
export const topUpApiBaseUrl = TOP_UP_API_BASE_URL;

export const topUpApi = new LegacyApiClient({
  baseUrl: topUpApiBaseUrl,
  getToken: sessionToken,
  onInvalidToken: endSession,
  timeoutMs: 15_000,
});
